/**
 * Canvas layout: a rooster "pen" card above a 5×3 nest grid, centered and
 * scaled to fit. Pure function of canvas size (CSS pixels) so hit-testing
 * and drawing always agree.
 */
export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface FarmLayout {
  width: number;
  height: number;
  pen: Rect;
  grid: { x: number; y: number; cols: number; rows: number; boxW: number; boxH: number; gap: number };
  /** Area where events and critters roam. */
  field: Rect;
  henSize: number;
  font: number;
}

export const COLS = 5;
export const ROWS = 3;

export function computeLayout(width: number, height: number): FarmLayout {
  const pad = Math.max(10, Math.min(width, height) * 0.03);
  const availW = width - pad * 2;
  const availH = height - pad * 2;
  // Vertical budget: pen (1.0 box) + gap + 3 rows + 2 gaps.
  let gap = 10;
  let boxW = Math.min((availW - (COLS - 1) * gap) / COLS, ((availH - 3 * gap) / 4.0) * 0.9);
  boxW = Math.min(boxW, 200);
  gap = Math.max(6, Math.min(16, boxW * 0.09));
  boxW = Math.min((availW - (COLS - 1) * gap) / COLS, ((availH - 3 * gap) / 4.0) * 0.9, 200);
  const boxH = boxW / 0.9;
  const gridW = COLS * boxW + (COLS - 1) * gap;
  const gridH = ROWS * boxH + (ROWS - 1) * gap;
  const penH = boxH * 1.0;
  const totalH = penH + gap + gridH;
  const x0 = (width - gridW) / 2;
  const y0 = pad + Math.max(0, (availH - totalH) / 2);
  return {
    width,
    height,
    pen: { x: x0, y: y0, w: gridW, h: penH },
    grid: { x: x0, y: y0 + penH + gap, cols: COLS, rows: ROWS, boxW, boxH, gap },
    field: { x: x0 - pad * 0.5, y: y0, w: gridW + pad, h: totalH },
    henSize: Math.floor(boxW * 0.78),
    font: Math.max(11, Math.min(16, Math.floor(boxW * 0.11))),
  };
}

export function boxRect(l: FarmLayout, index: number): Rect {
  const col = index % l.grid.cols;
  const row = Math.floor(index / l.grid.cols);
  return {
    x: l.grid.x + col * (l.grid.boxW + l.grid.gap),
    y: l.grid.y + row * (l.grid.boxH + l.grid.gap),
    w: l.grid.boxW,
    h: l.grid.boxH,
  };
}

export function roosterRect(l: FarmLayout): Rect {
  const size = l.pen.h * 0.9;
  return { x: l.pen.x + l.pen.h * 0.06, y: l.pen.y + (l.pen.h - size) / 2, w: size, h: size };
}

/** Map a normalized field point (0..1) to canvas pixels. */
export function fieldPoint(l: FarmLayout, nx: number, ny: number): { x: number; y: number } {
  return { x: l.field.x + nx * l.field.w, y: l.field.y + ny * l.field.h };
}

export function eventRadius(l: FarmLayout): number {
  return Math.max(13, l.grid.boxW * 0.13);
}

export function inRect(r: Rect, x: number, y: number): boolean {
  return x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;
}
